import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import {
  Users,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Home,
  Calendar,
  ChevronDown,
  ChevronRight,
  Search,
  RefreshCw,
  X,
  Check,
  AlertCircle,
  Loader2,
  MessageSquare,
  UserPlus,
  Clock,
  FileText,
  FileBarChart,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Target,
  Trophy,
  Send,
  ArrowUp,
  ArrowDown,
  BarChart3,
  Eye,
  Handshake,
  Building2,
  Paperclip,
  Upload,
  Download,
  User,
  Shield,
  MoreHorizontal,
  Smile,
} from "lucide-react";
import {
  fetchReferralLeads,
  updateLeadPartnerStatus,
  addPartnerNote,
  fetchPartnerNotes,
  uploadPartnerDocument,
  fetchPartnerDocuments,
} from "../api/referralTracker";
import { fetchPartners } from "../api/partners";
import { fetchTeamContext } from "../services/LeadsService";

// Status configuration for referral tracking
const STATUS_CONFIG = {
  new: {
    label: "New",
    color: "sky",
    bgClass: "bg-sky-100 dark:bg-sky-900/30",
    textClass: "text-sky-700 dark:text-sky-400",
    description: "Just received",
  },
  contacted: {
    label: "Contacted",
    color: "amber",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-400",
    description: "Reached out to buyer",
  },
  quoting: {
    label: "Quoting",
    color: "violet",
    bgClass: "bg-violet-100 dark:bg-violet-900/30",
    textClass: "text-violet-700 dark:text-violet-400",
    description: "Preparing quote",
  },
  quoted: {
    label: "Quoted",
    color: "indigo",
    bgClass: "bg-indigo-100 dark:bg-indigo-900/30",
    textClass: "text-indigo-700 dark:text-indigo-400",
    description: "Quote sent",
  },
  converted: {
    label: "Converted",
    color: "emerald",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/30",
    textClass: "text-emerald-700 dark:text-emerald-400",
    description: "Policy bound",
  },
  lost: {
    label: "Lost",
    color: "red",
    bgClass: "bg-red-100 dark:bg-red-900/30",
    textClass: "text-red-700 dark:text-red-400",
    description: "Not proceeding",
  },
};

// Quick reaction emojis for notes (like Slack)
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

// Celebration messages for insurance agents
const INSURANCE_CELEBRATION_MESSAGES = [
  "New Lead! 🛡️",
  "Coverage Ready! 🎯",
  "Policy Bound! ✅",
  "Protected! 🌟",
  "Quote Time! 📋",
  "Client Secured! ❤️",
  "Risk Covered! 🏠",
  "Deal Closed! 💎",
  "Premium Pro! 📈",
  "Top Agent! 🏆",
];

// Celebration Hook
const useInsuranceCelebration = () => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [recycleConfetti, setRecycleConfetti] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState(null);

  const triggerCelebration = useCallback(() => {
    setShowConfetti(false);
    setCelebrationMessage(null);

    setTimeout(() => {
      setShowConfetti(true);
      setRecycleConfetti(true);
    }, 10);

    const message = INSURANCE_CELEBRATION_MESSAGES[Math.floor(Math.random() * INSURANCE_CELEBRATION_MESSAGES.length)];
    setCelebrationMessage(message);

    setTimeout(() => setRecycleConfetti(false), 2500);
    setTimeout(() => {
      setShowConfetti(false);
      setCelebrationMessage(null);
    }, 5000);
  }, []);

  return { triggerCelebration, celebrationState: { showConfetti, recycleConfetti, celebrationMessage } };
};

// Celebration Overlay Component
const CelebrationOverlay = ({ state }) => {
  const { width, height } = useWindowSize();
  if (!state || (!state.showConfetti && !state.celebrationMessage)) return null;

  return (
    <>
      {state.showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
          <Confetti
            width={width}
            height={height}
            numberOfPieces={300}
            recycle={state.recycleConfetti}
            gravity={0.15}
            colors={['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899']}
          />
        </div>
      )}
      <AnimatePresence>
        {state.celebrationMessage && (
          <div className="fixed inset-0 flex items-center justify-center z-[100] pointer-events-none">
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 1.5, opacity: 0, filter: 'blur(10px)' }}
              transition={{ type: "spring", bounce: 0.5 }}
              className="text-6xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-500 to-yellow-500 drop-shadow-[0_0_30px_rgba(249,115,22,0.5)] tracking-tighter text-center px-4"
            >
              {state.celebrationMessage}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

// Stats Card Component
const StatsCard = ({ label, value, color, icon: Icon, isActive, onClick, trend, subValue }) => {
  const colorClasses = {
    sky: "bg-sky-500",
    amber: "bg-amber-500",
    violet: "bg-violet-500",
    emerald: "bg-emerald-500",
    slate: "bg-slate-500",
    indigo: "bg-indigo-500",
    red: "bg-red-500",
    orange: "bg-orange-500",
  };

  return (
    <button
      onClick={onClick}
      className={`flex-1 min-w-[140px] p-4 rounded-xl border transition-all ${
        isActive
          ? "bg-slate-900 dark:bg-white border-slate-900 dark:border-white"
          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="text-left">
          <div className="flex items-center gap-2">
            <p className={`text-2xl font-bold ${isActive ? "text-white dark:text-slate-900" : "text-slate-900 dark:text-white"}`}>
              {value}
            </p>
            {trend !== undefined && trend !== 0 && (
              <span className={`flex items-center text-xs font-medium ${trend > 0 ? "text-emerald-500" : "text-red-500"}`}>
                {trend > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                {Math.abs(trend)}%
              </span>
            )}
          </div>
          <p className={`text-xs font-medium ${isActive ? "text-slate-300 dark:text-slate-600" : "text-slate-500 dark:text-slate-400"}`}>
            {label}
          </p>
          {subValue && (
            <p className={`text-[10px] ${isActive ? "text-slate-400 dark:text-slate-500" : "text-slate-400 dark:text-slate-500"}`}>
              {subValue}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

// Status Badge Component
const StatusBadge = ({ status, size = "sm" }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm";

  return (
    <span className={`inline-flex items-center rounded-full font-medium ${sizeClasses} ${config.bgClass} ${config.textClass}`}>
      {config.label}
    </span>
  );
};

// Status Select Dropdown
const StatusSelect = ({ currentStatus, onStatusChange, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        disabled={isLoading}
        className="flex items-center gap-1 disabled:opacity-50"
      >
        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>
            <StatusBadge status={currentStatus} />
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute top-full mt-1 right-0 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-30 overflow-hidden"
          >
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <button
                key={key}
                onClick={(e) => { e.stopPropagation(); onStatusChange(key); setIsOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${
                  currentStatus === key ? "bg-slate-50 dark:bg-slate-700/50" : ""
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${config.bgClass}`} />
                <span className="text-slate-700 dark:text-slate-300">{config.label}</span>
                <span className="text-xs text-slate-400 ml-auto">{config.description}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Lead Row Component
const LeadRow = ({ lead, isSelected, onClick }) => {
  const sentDate = lead.sent_at ? new Date(lead.sent_at) : null;
  const status = lead.partner_status || 'new';

  const formatPrice = (price) => {
    if (!price) return "—";
    const match = price.match(/\$?([\d,]+)/g);
    if (match && match.length >= 1) {
      const values = match.map(v => {
        const num = parseInt(v.replace(/[$,]/g, ''));
        if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `$${Math.round(num / 1000)}K`;
        return `$${num}`;
      });
      return values.join('-');
    }
    return price;
  };

  return (
    <tr
      onClick={onClick}
      className={`cursor-pointer transition-colors ${
        isSelected ? "bg-orange-50 dark:bg-orange-900/20" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
      }`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-medium text-sm">
            {lead.buyer_name?.charAt(0)?.toUpperCase() || "B"}
          </div>
          <div>
            <p className="font-medium text-slate-900 dark:text-white">{lead.buyer_name || "Unknown Buyer"}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{lead.buyer_email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
          <Home className="w-3.5 h-3.5 text-slate-400" />
          {lead.property_type || "—"}
        </div>
        {lead.property_location && (
          <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
            <MapPin className="w-3 h-3" />
            {lead.property_location}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="text-sm font-medium text-slate-900 dark:text-white">
          {formatPrice(lead.price_range)}
        </span>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={status} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
          <Clock className="w-3.5 h-3.5" />
          {sentDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || "—"}
        </div>
      </td>
      <td className="px-4 py-3">
        <button className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-500 font-medium flex items-center gap-1">
          <Eye className="w-3.5 h-3.5" />
          View
        </button>
      </td>
    </tr>
  );
};

// Lead Detail Panel
const LeadDetailPanel = ({
  lead,
  onClose,
  onStatusChange,
  onAddNote,
  onUploadDocument,
  notes,
  documents,
  notesLoading,
  documentsLoading,
  statusLoading,
  partnerEmail,
  userName,
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [newNote, setNewNote] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showQuickReactions, setShowQuickReactions] = useState(null);
  const fileInputRef = useRef(null);

  if (!lead) return null;

  const status = lead.partner_status || 'new';
  const sentDate = lead.sent_at ? new Date(lead.sent_at) : null;

  const handleSubmitNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || isSubmittingNote) return;
    setIsSubmittingNote(true);
    try {
      await onAddNote(lead.lead_id || lead.email_id, newNote.trim());
      setNewNote("");
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      for (const file of files) {
        await onUploadDocument(lead.lead_id || lead.email_id, file);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const tabs = [
    { id: "details", label: "Details", icon: User },
    { id: "notes", label: "Notes", icon: MessageSquare, count: notes?.length },
    { id: "documents", label: "Documents", icon: Paperclip, count: documents?.length },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed right-0 top-0 bottom-0 z-[70] w-full max-w-lg bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-xl font-bold text-white">
                {lead.buyer_name?.charAt(0)?.toUpperCase() || "B"}
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  {lead.buyer_name || "Unknown Buyer"}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{lead.buyer_email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Status & Actions */}
          <div className="flex items-center justify-between">
            <StatusSelect
              currentStatus={status}
              onStatusChange={(newStatus) => onStatusChange(lead.lead_id || lead.email_id, newStatus)}
              isLoading={statusLoading}
            />
            <div className="flex gap-2">
              {lead.buyer_email && (
                <a
                  href={`mailto:${lead.buyer_email}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-sm rounded-lg"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </a>
              )}
              {lead.buyer_phone && (
                <a
                  href={`tel:${lead.buyer_phone}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <Phone className="w-4 h-4" />
                  Call
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative pb-3 pt-2 px-4 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "text-orange-600 dark:text-orange-400"
                  : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              <span className="flex items-center gap-1.5">
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="px-1.5 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-800">
                    {tab.count}
                  </span>
                )}
              </span>
              {activeTab === tab.id && (
                <motion.div layoutId="tab-indicator-insurance-detail" className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600" />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "details" && (
            <div className="space-y-6">
              {/* Lead Info */}
              <div>
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                  Buyer Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400">Name</p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.buyer_name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Email</p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{lead.buyer_email || "—"}</p>
                  </div>
                  {lead.buyer_phone && (
                    <div>
                      <p className="text-xs text-slate-400">Phone</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.buyer_phone}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-slate-400">Received</p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {sentDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) || "—"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Property Info */}
              <div>
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                  Property & Coverage
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {lead.property_type && (
                    <div>
                      <p className="text-xs text-slate-400">Property Type</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.property_type}</p>
                    </div>
                  )}
                  {lead.property_location && (
                    <div>
                      <p className="text-xs text-slate-400">Location</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.property_location}</p>
                    </div>
                  )}
                  {lead.price_range && (
                    <div>
                      <p className="text-xs text-slate-400">Property Value</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.price_range}</p>
                    </div>
                  )}
                  {lead.timeline && (
                    <div>
                      <p className="text-xs text-slate-400">Timeline</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.timeline}</p>
                    </div>
                  )}
                  {lead.loan_purpose && (
                    <div className="col-span-2">
                      <p className="text-xs text-slate-400">Purpose</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{lead.loan_purpose}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Referral Source */}
              <div>
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                  Referral Source
                </h3>
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <Handshake className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {lead.lender_name || "CloudMortgage Partner"}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Referring Lender</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "notes" && (
            <div className="space-y-4">
              {/* Add Note Form */}
              <form onSubmit={handleSubmitNote} className="flex gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this lead..."
                  className="flex-1 px-4 py-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="submit"
                  disabled={!newNote.trim() || isSubmittingNote}
                  className="px-4 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-xl disabled:opacity-50 transition-colors"
                >
                  {isSubmittingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>

              {/* Notes List */}
              {notesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                </div>
              ) : notes?.length > 0 ? (
                <div className="space-y-0">
                  {notes.map((note, idx) => (
                    <div key={note.id || idx} className="relative pl-6 pb-4 border-l-2 border-slate-200 dark:border-slate-700 last:pb-0">
                      {/* Timeline dot */}
                      <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-amber-500" />

                      {/* Note Card */}
                      <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                        {/* Note Content */}
                        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{note.note || note.content}</p>

                        {/* Author & Timestamp */}
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                          <span className="font-medium text-slate-600 dark:text-slate-300">
                            {note.author || note.created_by || note.user_name || userName || partnerEmail || 'Unknown'}
                          </span>
                          <span>•</span>
                          <span>
                            {note.created_at ? new Date(note.created_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                            }) : 'Just now'}
                          </span>
                        </div>

                        {/* Reactions Section */}
                        <div className="flex items-center gap-2 mt-2 relative">
                          {/* Existing Reactions */}
                          {note.reactions && note.reactions.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {note.reactions.map((reaction) => (
                                <button
                                  key={reaction.emoji}
                                  className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 transition ${
                                    reaction.userReacted
                                      ? 'bg-amber-600/30 border border-amber-600/50 text-amber-700 dark:text-white'
                                      : 'dark:bg-slate-700/80 bg-slate-100 border dark:border-slate-600/50 border-slate-200 dark:text-slate-300 text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600'
                                  }`}
                                  title={reaction.users?.join(', ') || ''}
                                >
                                  <span>{reaction.emoji}</span>
                                  <span className="font-medium">{reaction.count}</span>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Add Reaction Button */}
                          <button
                            onClick={() => setShowQuickReactions(showQuickReactions === idx ? null : idx)}
                            className="p-1 text-slate-400 hover:text-yellow-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                            title="Add reaction"
                          >
                            <Smile className="w-4 h-4" />
                          </button>

                          {/* Quick Reactions Popup */}
                          <AnimatePresence>
                            {showQuickReactions === idx && (
                              <>
                                {/* Backdrop to close on click outside */}
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => setShowQuickReactions(null)}
                                />
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.95, y: -5 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  exit={{ opacity: 0, scale: 0.95, y: -5 }}
                                  className="absolute left-0 bottom-full mb-1 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-2 flex gap-1 z-50"
                                >
                                  {QUICK_REACTIONS.map(emoji => (
                                    <button
                                      key={emoji}
                                      onClick={() => {
                                        // TODO: Implement reaction API call
                                        console.log('Reaction:', emoji, 'on note:', idx);
                                        setShowQuickReactions(null);
                                      }}
                                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-all hover:scale-125"
                                    >
                                      <span className="text-xl">{emoji}</span>
                                    </button>
                                  ))}
                                </motion.div>
                              </>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">No notes yet</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Add your first note above</p>
                </div>
              )}
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-4">
              {/* Upload Area */}
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFileUpload(e.dataTransfer.files); }}
                className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center hover:border-orange-500 transition-colors"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                />
                {isUploading ? (
                  <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                      Drag & drop files or{" "}
                      <button onClick={() => fileInputRef.current?.click()} className="text-orange-600 hover:text-orange-500 font-medium">
                        browse
                      </button>
                    </p>
                    <p className="text-xs text-slate-400">PDF, Images, Documents up to 10MB</p>
                  </>
                )}
              </div>

              {/* Documents List */}
              {documentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                </div>
              ) : documents?.length > 0 ? (
                <div className="space-y-2">
                  {documents.map((doc, idx) => (
                    <div key={doc.id || idx} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{doc.filename || doc.name}</p>
                        <p className="text-xs text-slate-400">{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'Just uploaded'}</p>
                      </div>
                      {doc.url && (
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:text-orange-500">
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Paperclip className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">No documents uploaded</p>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Report Card Component
const ReportCard = ({ report, onClick, isSelected }) => {
  const date = report.date ? new Date(report.date) : new Date();

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all ${
        isSelected
          ? "bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700"
          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-900 dark:text-white">
          {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
        </span>
        {report.new_leads > 0 && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
            +{report.new_leads} new
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-bold text-slate-900 dark:text-white">{report.total_leads || 0}</p>
          <p className="text-[10px] text-slate-400">Total</p>
        </div>
        <div>
          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{report.converted || 0}</p>
          <p className="text-[10px] text-slate-400">Converted</p>
        </div>
        <div>
          <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{report.pipeline_value || "$0"}</p>
          <p className="text-[10px] text-slate-400">Pipeline</p>
        </div>
      </div>
    </button>
  );
};

// Empty State
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 flex items-center justify-center mb-4">
      <Shield className="w-10 h-10 text-orange-600 dark:text-orange-400" />
    </div>
    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No leads yet</h3>
    <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-sm mb-6">
      When lenders send you insurance referrals, they'll appear here. You can track progress, add notes, and manage your pipeline.
    </p>
    <div className="flex items-center gap-2 text-sm text-slate-400">
      <Sparkles className="w-4 h-4" />
      <span>Referrals are on their way!</span>
    </div>
  </div>
);

// Main Component
// Helper to convert email to display name
const getDisplayNameFromEmail = (email) => {
  if (!email) return 'Unknown';
  const localPart = email.split('@')[0];
  return localPart
    .split(/[._-]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const InsuranceReferralDashboard = ({ userEmail }) => {
  const { width } = useWindowSize();
  const { triggerCelebration, celebrationState } = useInsuranceCelebration();

  // Compute display name from email
  const userName = useMemo(() => getDisplayNameFromEmail(userEmail), [userEmail]);

  // State
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("leads");

  // Selected lead state
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadNotes, setLeadNotes] = useState({});
  const [leadDocuments, setLeadDocuments] = useState({});
  const [notesLoading, setNotesLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // Reports state
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);

  // Insurance Agents state
  const [insuranceAgents, setInsuranceAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentSearchTerm, setAgentSearchTerm] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentLeads, setAgentLeads] = useState([]);
  const [agentLeadsLoading, setAgentLeadsLoading] = useState(false);
  const [allAgentLeads, setAllAgentLeads] = useState([]);
  const [totalPipelineValue, setTotalPipelineValue] = useState(0);

  // Team context state - for team owners to see all team leads
  const [teamContext, setTeamContext] = useState(null);

  // Calculate stats
  const stats = useMemo(() => {
    const total = leads.length;
    const byStatus = Object.keys(STATUS_CONFIG).reduce((acc, status) => {
      acc[status] = leads.filter(l => (l.partner_status || 'new') === status).length;
      return acc;
    }, {});

    // Calculate pipeline value
    const activePipeline = leads.filter(l => !['converted', 'lost'].includes(l.partner_status || 'new'));
    const pipelineValue = activePipeline.reduce((sum, lead) => {
      const match = (lead.price_range || "").match(/\$?([\d,]+)/);
      if (match) {
        return sum + parseInt(match[1].replace(/,/g, ''));
      }
      return sum;
    }, 0);

    // Calculate conversion rate
    const conversionRate = total > 0 ? Math.round((byStatus.converted / total) * 100) : 0;

    return { total, byStatus, pipelineValue, conversionRate, activePipeline: activePipeline.length };
  }, [leads]);

  // Daily reports - will be fetched from API when available
  const dailyReports = [];

  // Fetch leads
  const fetchLeads = useCallback(async (showRefresh = false) => {
    if (!userEmail) return;

    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    setError(null);

    try {
      const response = await fetchReferralLeads(userEmail, 'insurance', {
        limit: 200,
        team_owner_id: teamContext?.team_owner_id
      });
      setLeads(response.emails || []);
    } catch (err) {
      console.error("Failed to fetch leads:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [userEmail, teamContext]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Load team context to determine if user is team owner
  useEffect(() => {
    const loadTeamContext = async () => {
      try {
        const response = await fetchTeamContext();
        console.log("[InsuranceDashboard] Team Context:", response);
        if (response.success && response.data) {
          setTeamContext(response.data);
        }
      } catch (err) {
        console.error("[InsuranceDashboard] Failed to load team context:", err);
      }
    };
    loadTeamContext();
  }, []);

  // Fetch insurance agents and their leads to calculate total pipeline
  const fetchAgentsList = useCallback(async () => {
    setAgentsLoading(true);
    try {
      const response = await fetchPartners();
      const allPartners = response?.data?.partners || response?.partners || [];
      // Filter to only show insurance agents
      const agents = allPartners.filter(p => p.partner_type === 'insurance_agent');

      // Fetch leads for all agents to calculate metrics per agent
      let allLeads = [];
      const leadsPromises = agents.map(async (agent) => {
        try {
          const leadsResponse = await fetchReferralLeads(agent.email, 'insurance', {
            limit: 100,
            team_owner_id: teamContext?.team_owner_id
          });
          const leads = leadsResponse.emails || [];
          console.log(`[InsuranceDashboard] Agent ${agent.email}: ${leads.length} leads fetched`);
          return { email: agent.email, leads };
        } catch (err) {
          console.error(`[InsuranceDashboard] Error fetching leads for ${agent.email}:`, err);
          return { email: agent.email, leads: [] };
        }
      });

      const leadsResults = await Promise.all(leadsPromises);

      // Create a map of leads per agent
      const leadsMap = {};
      leadsResults.forEach(result => {
        leadsMap[result.email] = result.leads;
        allLeads = allLeads.concat(result.leads);
      });
      console.log('[InsuranceDashboard] Total leads across all agents:', allLeads.length);
      setAllAgentLeads(allLeads);

      // Enrich agents with their metrics
      const enrichedAgents = agents.map(agent => {
        const agentLeadsList = leadsMap[agent.email] || [];
        const leadsSent = agentLeadsList.length;
        const leadsConverted = agentLeadsList.filter(l => l.partner_status === 'converted').length;
        const pipelineValue = agentLeadsList.reduce((sum, lead) => {
          if (['converted', 'lost'].includes(lead.partner_status)) return sum;
          const match = (lead.price_range || "").match(/\$?([\d,]+)/);
          if (match) {
            return sum + parseInt(match[1].replace(/,/g, ''));
          }
          return sum;
        }, 0);

        return {
          ...agent,
          leadsSent,
          leadsConverted,
          pipelineValue,
        };
      });

      setInsuranceAgents(enrichedAgents);

      // Calculate total pipeline value
      const pipelineTotal = allLeads.reduce((sum, lead) => {
        if (['converted', 'lost'].includes(lead.partner_status)) return sum;
        const match = (lead.price_range || "").match(/\$?([\d,]+)/);
        if (match) {
          return sum + parseInt(match[1].replace(/,/g, ''));
        }
        return sum;
      }, 0);
      setTotalPipelineValue(pipelineTotal);
    } catch (err) {
      console.error("Failed to fetch insurance agents:", err);
    } finally {
      setAgentsLoading(false);
    }
  }, [teamContext]);

  // Load agents when team context is available
  useEffect(() => {
    // Wait for team context to load before fetching agents
    // This ensures we have team_owner_id for the API calls
    if (teamContext && insuranceAgents.length === 0) {
      fetchAgentsList();
    }
  }, [teamContext, insuranceAgents.length, fetchAgentsList]);

  // Filter agents by search
  const filteredAgents = useMemo(() => {
    if (!agentSearchTerm) return insuranceAgents;
    const search = agentSearchTerm.toLowerCase();
    return insuranceAgents.filter(a =>
      a.name?.toLowerCase().includes(search) ||
      a.email?.toLowerCase().includes(search) ||
      a.company?.toLowerCase().includes(search)
    );
  }, [insuranceAgents, agentSearchTerm]);

  // Handle agent selection - fetch leads sent to this agent
  const handleSelectAgent = useCallback(async (agent) => {
    console.log('[InsuranceDashboard] Selecting agent:', agent.email);
    console.log('[InsuranceDashboard] Current user email:', userEmail);
    setSelectedAgent(agent);
    setAgentLeads([]);
    setAgentLeadsLoading(true);
    try {
      console.log('[InsuranceDashboard] Fetching leads for agent:', agent.email);
      console.log('[InsuranceDashboard] Team context:', teamContext);
      const response = await fetchReferralLeads(agent.email, 'insurance', {
        limit: 100,
        team_owner_id: teamContext?.team_owner_id
      });
      console.log('[InsuranceDashboard] API response:', response);
      console.log('[InsuranceDashboard] Leads count:', response.emails?.length || 0);
      setAgentLeads(response.emails || []);
    } catch (err) {
      console.error("[InsuranceDashboard] Failed to fetch agent leads:", err);
    } finally {
      setAgentLeadsLoading(false);
    }
  }, [userEmail, teamContext]);

  // Handle lead selection
  const handleSelectLead = useCallback(async (lead) => {
    setSelectedLead(lead);
    const leadId = lead.lead_id || lead.email_id;

    // Fetch notes
    if (!leadNotes[leadId]) {
      setNotesLoading(true);
      try {
        const response = await fetchPartnerNotes(leadId, userEmail);
        setLeadNotes(prev => ({ ...prev, [leadId]: response.notes || [] }));
      } catch (err) {
        console.error("Failed to fetch notes:", err);
        setLeadNotes(prev => ({ ...prev, [leadId]: [] }));
      } finally {
        setNotesLoading(false);
      }
    }

    // Fetch documents
    if (!leadDocuments[leadId]) {
      setDocumentsLoading(true);
      try {
        const response = await fetchPartnerDocuments(leadId, userEmail);
        setLeadDocuments(prev => ({ ...prev, [leadId]: response.documents || [] }));
      } catch (err) {
        console.error("Failed to fetch documents:", err);
        setLeadDocuments(prev => ({ ...prev, [leadId]: [] }));
      } finally {
        setDocumentsLoading(false);
      }
    }
  }, [leadNotes, leadDocuments, userEmail]);

  // Handle status change
  const handleStatusChange = async (leadId, newStatus) => {
    setStatusLoading(true);
    try {
      await updateLeadPartnerStatus(leadId, newStatus, userEmail);
      setLeads(prev => prev.map(l => (l.lead_id || l.email_id) === leadId ? { ...l, partner_status: newStatus } : l));
      if (selectedLead && (selectedLead.lead_id || selectedLead.email_id) === leadId) {
        setSelectedLead(prev => ({ ...prev, partner_status: newStatus }));
      }
      if (newStatus === 'converted') {
        triggerCelebration();
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setStatusLoading(false);
    }
  };

  // Handle add note
  const handleAddNote = async (leadId, note) => {
    const authorName = userName;

    try {
      const response = await addPartnerNote(leadId, note, userEmail);
      // Always include author from local computation (API may not return it)
      const newNote = {
        note,
        author: authorName,
        created_by: userEmail,
        created_at: new Date().toISOString(),
        id: Date.now(),
        ...(response.note || {}),
      };
      // Ensure author is always set even if API returns empty author
      if (!newNote.author) newNote.author = authorName;
      setLeadNotes(prev => ({
        ...prev,
        [leadId]: [newNote, ...(prev[leadId] || [])],
      }));
    } catch (err) {
      console.error("Failed to add note:", err);
      // Add note locally even if API fails (for demo/offline mode)
      setLeadNotes(prev => ({
        ...prev,
        [leadId]: [{ note, author: authorName, created_by: userEmail, created_at: new Date().toISOString(), id: Date.now() }, ...(prev[leadId] || [])],
      }));
    }
  };

  // Handle upload document
  const handleUploadDocument = async (leadId, file) => {
    try {
      const response = await uploadPartnerDocument(leadId, file, userEmail);
      setLeadDocuments(prev => ({
        ...prev,
        [leadId]: [response.document || { filename: file.name, created_at: new Date().toISOString() }, ...(prev[leadId] || [])],
      }));
    } catch (err) {
      console.error("Failed to upload document:", err);
      // Add document locally even if API fails (for demo/offline mode)
      setLeadDocuments(prev => ({
        ...prev,
        [leadId]: [{ filename: file.name, created_at: new Date().toISOString(), id: Date.now() }, ...(prev[leadId] || [])],
      }));
    }
  };

  // Filter leads
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const search = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || (
        lead.buyer_name?.toLowerCase().includes(search) ||
        lead.buyer_email?.toLowerCase().includes(search) ||
        lead.property_type?.toLowerCase().includes(search) ||
        lead.property_location?.toLowerCase().includes(search)
      );
      const leadStatus = lead.partner_status || 'new';
      const matchesStatus = statusFilter === 'all' || leadStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [leads, searchTerm, statusFilter]);

  // Format currency
  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${Math.round(value / 1000)}K`;
    return `$${value}`;
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col min-h-0 animate-pulse">
        {/* Skeleton Header */}
        <div className="flex-none p-6 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-yellow-500/10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-orange-500/30 rounded" />
                <div className="h-7 w-64 bg-slate-300 dark:bg-slate-700 rounded" />
              </div>
              <div className="h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded mt-2" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/30 to-amber-500/30" />
                <div>
                  <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-1" />
                  <div className="h-8 w-24 bg-orange-500/30 rounded" />
                </div>
              </div>
              <div className="w-9 h-9 bg-slate-200 dark:bg-slate-700 rounded-lg" />
            </div>
          </div>
          {/* Skeleton Tabs */}
          <div className="flex gap-1 mt-4 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg w-fit">
            <div className="w-24 h-9 bg-slate-200 dark:bg-slate-700 rounded-md" />
            <div className="w-28 h-9 bg-slate-200 dark:bg-slate-700 rounded-md" />
            <div className="w-24 h-9 bg-slate-200 dark:bg-slate-700 rounded-md" />
          </div>
        </div>

        {/* Skeleton Stats Cards */}
        <div className="flex-none p-4 border-b border-slate-200 dark:border-slate-800">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="bg-slate-100 dark:bg-slate-800/50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 bg-slate-300 dark:bg-slate-600 rounded" />
                  <div className="h-3 w-16 bg-slate-300 dark:bg-slate-600 rounded" />
                </div>
                <div className="h-6 w-10 bg-slate-300 dark:bg-slate-600 rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Skeleton Content Area */}
        <div className="flex-1 p-4 overflow-hidden">
          <div className="flex gap-3 mb-4">
            <div className="flex-1 h-10 bg-slate-200 dark:bg-slate-700 rounded-lg" />
            <div className="w-32 h-10 bg-slate-200 dark:bg-slate-700 rounded-lg" />
          </div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-slate-100 dark:bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-300 dark:bg-slate-600 rounded-full" />
                    <div>
                      <div className="h-4 w-32 bg-slate-300 dark:bg-slate-600 rounded mb-2" />
                      <div className="h-3 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-20 bg-orange-500/30 rounded-full" />
                    <div className="h-6 w-24 bg-slate-300 dark:bg-slate-600 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Loading indicator */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-slate-200 dark:border-slate-700">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-slate-900 dark:text-white">Loading Insurance Referrals</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Fetching your pipeline data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <CelebrationOverlay state={celebrationState} />

      {/* Header */}
      <div className="flex-none p-6 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-yellow-500/10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Shield className="w-6 h-6 text-orange-600" />
              Insurance Referral Center
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Manage insurance referrals and track your pipeline
            </p>
          </div>

          {/* Pipeline Value Display */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-widest">Pipeline Value</p>
                <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400">
                  {formatCurrency(totalPipelineValue || stats.pipelineValue)}
                </p>
              </div>
            </div>

            <button
              onClick={() => fetchLeads(true)}
              disabled={isRefreshing}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab("leads")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === "leads"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <Users className="w-4 h-4 inline mr-1.5" />
            My Leads
          </button>
          <button
            onClick={() => setActiveTab("reports")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === "reports"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <FileBarChart className="w-4 h-4 inline mr-1.5" />
            Daily Reports
          </button>
          <button
            onClick={() => setActiveTab("agents")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === "agents"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <Shield className="w-4 h-4 inline mr-1.5" />
            Insurance Agents
            {insuranceAgents.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                {insuranceAgents.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mx-6 mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === "leads" ? (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="flex flex-wrap gap-3">
              <StatsCard
                label="All Leads"
                value={stats.total}
                color="slate"
                icon={Users}
                isActive={statusFilter === "all"}
                onClick={() => setStatusFilter("all")}
              />
              <StatsCard
                label="New"
                value={stats.byStatus.new || 0}
                color="sky"
                icon={UserPlus}
                isActive={statusFilter === "new"}
                onClick={() => setStatusFilter("new")}
                subValue="Awaiting contact"
              />
              <StatsCard
                label="Contacted"
                value={stats.byStatus.contacted || 0}
                color="amber"
                icon={Phone}
                isActive={statusFilter === "contacted"}
                onClick={() => setStatusFilter("contacted")}
              />
              <StatsCard
                label="Quoting"
                value={stats.byStatus.quoting || 0}
                color="violet"
                icon={FileText}
                isActive={statusFilter === "quoting"}
                onClick={() => setStatusFilter("quoting")}
              />
              <StatsCard
                label="Converted"
                value={stats.byStatus.converted || 0}
                color="emerald"
                icon={Trophy}
                isActive={statusFilter === "converted"}
                onClick={() => setStatusFilter("converted")}
                subValue={`${stats.conversionRate}% rate`}
              />
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search leads by name, email, property..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Leads Table */}
            {filteredLeads.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900/50">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Buyer</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Property</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Value</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Received</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {filteredLeads.map((lead) => (
                      <LeadRow
                        key={lead.lead_id || lead.email_id}
                        lead={lead}
                        isSelected={selectedLead?.email_id === lead.email_id}
                        onClick={() => handleSelectLead(lead)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : activeTab === "reports" ? (
          /* Reports Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Daily Performance Reports
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Track your referral performance over time
                </p>
              </div>
            </div>

            {/* Empty State */}
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                <FileBarChart className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                No reports yet
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md">
                Daily performance reports will appear here once they are available.
              </p>
            </div>
          </div>
        ) : activeTab === "agents" ? (
          /* Insurance Agents Tab */
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-orange-500" />
                  Insurance Agent Partners
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {insuranceAgents.length} insurance agent{insuranceAgents.length !== 1 ? 's' : ''} in your network
                </p>
              </div>
              <button
                onClick={fetchAgentsList}
                disabled={agentsLoading}
                className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${agentsLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search agents by name, email, or company..."
                value={agentSearchTerm}
                onChange={(e) => setAgentSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Agents Table */}
            {agentsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  {agentSearchTerm ? 'No agents found' : 'No insurance agents yet'}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {agentSearchTerm ? 'Try a different search term' : 'Insurance agent partners will appear here'}
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900/50">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Partner</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Company</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Leads Sent</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Converted</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Pipeline</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Status</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {filteredAgents.map((agent) => (
                      <tr
                        key={agent.email}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                        onClick={() => handleSelectAgent(agent)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-medium text-sm">
                              {agent.name?.charAt(0)?.toUpperCase() || agent.email?.charAt(0)?.toUpperCase() || "I"}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900 dark:text-white">{agent.name || "Unknown"}</p>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                                  <Shield className="w-2.5 h-2.5 mr-0.5" />
                                  Insurance
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{agent.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600 dark:text-slate-300">{agent.company || "—"}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium text-sky-400">{agent.leadsSent || 0}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium text-emerald-400">{agent.leadsConverted || 0}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium text-amber-400">{formatCurrency(agent.pipelineValue || 0)}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            agent.status === 'active'
                              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          }`}>
                            {agent.status === 'active' ? 'Active' : 'Paused'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); }}
                            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Lead Detail Panel */}
      {selectedLead && (
        <LeadDetailPanel
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onStatusChange={handleStatusChange}
          onAddNote={handleAddNote}
          onUploadDocument={handleUploadDocument}
          notes={leadNotes[selectedLead.lead_id || selectedLead.email_id] || []}
          documents={leadDocuments[selectedLead.lead_id || selectedLead.email_id] || []}
          notesLoading={notesLoading}
          documentsLoading={documentsLoading}
          statusLoading={statusLoading}
          partnerEmail={userEmail}
          userName={userName}
        />
      )}

      {/* Agent Leads Panel */}
      <AnimatePresence>
        {selectedAgent && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm"
              onClick={() => { setSelectedAgent(null); setSelectedLead(null); }}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-2xl bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="flex-none p-6 border-b border-slate-200 dark:border-slate-800">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-xl font-bold text-white">
                      {selectedAgent.name?.charAt(0)?.toUpperCase() || "I"}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                          {selectedAgent.name || selectedAgent.email}
                        </h2>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                          <Shield className="w-3 h-3 mr-0.5" />
                          Insurance
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{selectedAgent.email}</p>
                      {selectedAgent.company && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                          <Building2 className="w-3.5 h-3.5" />
                          {selectedAgent.company}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedAgent(null); setSelectedLead(null); }}
                    className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Stats */}
                <div className="flex gap-4 mt-4">
                  <div className="flex-1 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-center">
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{agentLeads.length}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Leads Sent</p>
                  </div>
                  <div className="flex-1 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-center">
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {agentLeads.filter(l => l.partner_status === 'converted').length}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Converted</p>
                  </div>
                  <div className="flex-1 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-center">
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      ${agentLeads.reduce((sum, l) => {
                        const match = (l.price_range || "").match(/\$?([\d,]+)/);
                        return sum + (match ? parseInt(match[1].replace(/,/g, '')) / 1000 : 0);
                      }, 0).toFixed(0)}K
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Pipeline</p>
                  </div>
                </div>
              </div>

              {/* Leads List */}
              <div className="flex-1 overflow-auto p-6">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-orange-500" />
                  Leads Sent to {selectedAgent.name?.split(' ')[0] || 'Agent'}
                </h3>

                {agentLeadsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                  </div>
                ) : agentLeads.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                      <Mail className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400">No leads sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {agentLeads.map((lead, idx) => {
                      const sentDate = lead.sent_at ? new Date(lead.sent_at) : null;
                      const status = lead.partner_status || 'new';
                      const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.new;

                      return (
                        <div
                          key={lead.email_id || idx}
                          onClick={() => handleSelectLead(lead)}
                          className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-orange-300 dark:hover:border-orange-700 cursor-pointer transition-all group"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-medium">
                                {lead.buyer_name?.charAt(0)?.toUpperCase() || "B"}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                                  {lead.buyer_name || "Unknown Buyer"}
                                </p>
                                <p className="text-sm text-slate-500 dark:text-slate-400">{lead.buyer_email}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgClass} ${statusConfig.textClass}`}>
                                {statusConfig.label}
                              </span>
                              {sentDate && (
                                <p className="text-xs text-slate-400 mt-1">
                                  {sentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Lead Details */}
                          <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 grid grid-cols-3 gap-4 text-sm">
                            {lead.property_type && (
                              <div>
                                <p className="text-slate-400 text-xs">Property</p>
                                <p className="text-slate-700 dark:text-slate-300 flex items-center gap-1">
                                  <Home className="w-3 h-3" />
                                  {lead.property_type}
                                </p>
                              </div>
                            )}
                            {lead.price_range && (
                              <div>
                                <p className="text-slate-400 text-xs">Value</p>
                                <p className="text-slate-700 dark:text-slate-300 font-medium">{lead.price_range}</p>
                              </div>
                            )}
                            {lead.property_location && (
                              <div>
                                <p className="text-slate-400 text-xs">Location</p>
                                <p className="text-slate-700 dark:text-slate-300 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {lead.property_location}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Quick Actions */}
                          <div className="mt-3 flex gap-2">
                            {lead.buyer_email && (
                              <a
                                href={`mailto:${lead.buyer_email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
                              >
                                <Mail className="w-3 h-3" />
                                Email
                              </a>
                            )}
                            {lead.buyer_phone && (
                              <a
                                href={`tel:${lead.buyer_phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                              >
                                <Phone className="w-3 h-3" />
                                Call
                              </a>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSelectLead(lead); }}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors ml-auto"
                            >
                              <Eye className="w-3 h-3" />
                              View Details
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InsuranceReferralDashboard;
